/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {InjectionToken} from '@angular/core';
/**
 * This class should not be used directly by an application developer. Instead, use
 * {\@link Location}.
 * 
 * `PlatformLocation` encapsulates all calls to DOM apis, which allows the Router to be platform
 * agnostic.
 * This means that we can have different implementation of `PlatformLocation` for the different
 * platforms that angular supports. For example, `\@angular/platform-browser` provides an
 * implementation specific to the browser environment, while `\@angular/platform-webworker` provides
 * one suitable for use with web workers.
 * 
 * The `PlatformLocation` class is used directly by all implementations of {\@link LocationStrategy}
 * when they need to interact with the DOM apis like pushState, popState, etc...
 * 
 * {\@link LocationStrategy} in turn is used by the {\@link Location} service which is used directly
 * by the {\@link Router} in order to navigate between routes. Since all interactions between {\@link
 * Router} /
 * {\@link Location} / {\@link LocationStrategy} and DOM apis flow through the `PlatformLocation`
 * class they are all platform independent.
 * 
 * \@stable
 * @abstract
 */
export abstract class PlatformLocation {
/**
 * @abstract
 * @return {?}
 */
getBaseHrefFromDOM() {}
/**
 * @abstract
 * @param {?} fn
 * @return {?}
 */
onPopState(fn: LocationChangeListener) {}
/**
 * @abstract
 * @param {?} fn
 * @return {?}
 */
onHashChange(fn: LocationChangeListener) {}
/**
 * @abstract
 * @return {?}
 */
pathname() {}
/**
 * @abstract
 * @return {?}
 */
search() {}
/**
 * @abstract
 * @return {?}
 */
hash() {}
/**
 * @abstract
 * @param {?} state
 * @param {?} title
 * @param {?} url
 * @return {?}
 */
replaceState(state: any, title: string, url: string) {}
/**
 * @abstract
 * @param {?} state
 * @param {?} title
 * @param {?} url
 * @return {?}
 */
pushState(state: any, title: string, url: string) {}
/**
 * @abstract
 * @return {?}
 */
forward() {}
/**
 * @abstract
 * @return {?}
 */
back() {}
}
/**
 * \@whatItDoes indicates when a location is initialized
 * \@experimental
 */
export const LOCATION_INITIALIZED = new InjectionToken<Promise<any>>('Location Initialized');

/**
 * A serializable version of the event from onPopState or onHashChange
 *
 * @experimental
 */
export interface LocationChangeEvent { type: string; }

/**
 * @experimental
 */
export interface LocationChangeListener { (e: LocationChangeEvent): any; }
